import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { UserSelectValue } from '@dntlab/share-files-dialog';
import { firstValueFrom, Observable, Subject } from 'rxjs';
import { SessionService } from 'src/app/auth/session.service';
import { AppGlobalControlValues } from 'src/app/common/app.global.control.values';
import { promiseWithCatch } from 'src/app/common/app.helpers';
import { OperationInfo } from 'src/app/common/long-operations/long-operations.interface';
import { OperationInfoService } from 'src/app/common/long-operations/operation-info.service';
import { SendMessageDialogResult } from 'src/app/interfaces';
import {
  Conversation,
  ConversationKinds,
  CreateConversationRequest,
  FullUser,
  DefaultService as RestApiService,
  UserBasic,
} from 'src/app/swagger-api';
import { ChatEngineChannelsService } from '../interfaces/chat-engine/chat-engine-channels.service';
import { ChatEngineErrorService, ChatEngineOperations } from '../interfaces/chat-engine/chat-engine-error.service';
import { ChatEngineSendMessageService } from '../interfaces/chat-engine/chat-engine-send-message.service';
import { ChatConversation, ChatTraceData } from '../interfaces/chat-model.interface';
import { CreateConversationContent } from '../interfaces/chat-operations.interface';
import { ChatDialogsService } from './chat-dialogs.service';

@Injectable({ providedIn: 'root' })
export class ChatSendMessageToService {
  private chatDialogsService = inject(ChatDialogsService);
  private chatEngineSendMessageService = inject(ChatEngineSendMessageService);
  private chatEngineChannelsService = inject(ChatEngineChannelsService);
  private chatEngineErrorService = inject(ChatEngineErrorService);
  private sessionService = inject(SessionService);
  private apiService = inject(RestApiService);
  private operationInfoService = inject(OperationInfoService);

  public sendMessageToProfiles(profiles: UserBasic[], showConfirmation: boolean): Observable<ChatConversation> {
    const response$: Subject<ChatConversation> = new Subject<ChatConversation>();
    const operation: OperationInfo = {
      spinner: 'button',
      button: { id: 'btn-send-message' },
      blockedUIControls: [{ elementId: AppGlobalControlValues.LOG_OUT_BUTTON }],
    };

    this.chatDialogsService.showSendMessageDialogWithSelectedUsers(profiles, (data: SendMessageDialogResult) => {
      this.operationInfoService.setupOperation(operation);
      this.sendMessageDialogResponseHandler(data, showConfirmation)
        .then(
          (conversation: ChatConversation) => response$.next(conversation),
          (error: Error) => {
            if (!(error instanceof HttpErrorResponse)) {
              this.chatEngineErrorService.onOperationFailed(
                ChatEngineOperations.SEND_MESSAGE_TO_PROFILE,
                error,
                {} as ChatTraceData,
              );
              this.chatDialogsService.showFailedToSendMessage();
              response$.error(error);
            } else {
              // HTTP errors have been already handled at interceptors level
              response$.complete();
            }
          },
        )
        .finally(() => this.operationInfoService.stopLoading(operation));
    });
    return response$.asObservable();
  }

  public sendMessageToNotYetSelectedUsers(showConfirmation: boolean): Observable<ChatConversation> {
    const response$: Subject<ChatConversation> = new Subject<ChatConversation>();

    this.chatDialogsService.showSendMessageDialogWithoutSelectedUsers(
      (data: SendMessageDialogResult) =>
        void this.sendMessageDialogResponseHandler(data, showConfirmation).then(
          (conversation: ChatConversation) => response$.next(conversation),
          (error: Error) => {
            this.chatEngineErrorService.onOperationFailed(
              ChatEngineOperations.SEND_MESSAGE_NEW_DIALOG,
              error,
              {} as ChatTraceData,
            );
            this.chatDialogsService.showFailedToSendMessage();
            response$.error(error);
          },
        ),
    );
    return response$.asObservable();
  }

  private async getPrivateConversation(participantId: string): Promise<string | null> {
    const participantProfile: FullUser = await firstValueFrom(this.apiService.restUserGet(participantId));

    return participantProfile.chat ? participantProfile.chat : null;
  }

  /**
   * @description Processes next send message cases after dialog response was retrieved.
   * @param { SendMessageDialogResult } data
   * @param { boolean } showConfirmation Optional. Defines if the 'message sent'
   *                                     confirmation dialog should be displayed.
   */
  private async sendMessageDialogResponseHandler(
    data: SendMessageDialogResult,
    showConfirmation?: boolean,
  ): Promise<ChatConversation> {
    const chatId: string | null = await this.getPrivateConversation(data.participants[0].id);

    if (data.participants.length === 1 && chatId) {
      promiseWithCatch(this.sendMessageToChannel(chatId, data.message, showConfirmation));

      return this.chatEngineChannelsService.getConversationByUniqueName(chatId);
    }

    return this.createConversationHandler(
      {
        usersIds: data.participants.map((value: UserSelectValue) => value.id),
        messageText: data.message,
      },
      showConfirmation,
    );
  }

  private async createConversationHandler(
    content: CreateConversationContent,
    showConfirmation?: boolean,
  ): Promise<ChatConversation> {
    const requestBody: CreateConversationRequest = {
      participant_user_ids: content.usersIds.concat([this.sessionService.userId]),
      kind: content.usersIds.length > 1 ? ConversationKinds.Group : ConversationKinds.Private,
    };

    const conversation: Conversation = await this.createConversation(requestBody);
    const channelUniqueName: string = conversation.messaging_channel_basic.channel_external_id;
    await this.sendMessageToChannel(channelUniqueName, content.messageText, showConfirmation);
    return this.chatEngineChannelsService.getConversationByUniqueName(channelUniqueName);
  }

  private createConversation(conversationBody: CreateConversationRequest): Promise<Conversation> {
    return firstValueFrom(this.apiService.restSocialConversationsPost(conversationBody));
  }

  private async sendMessageToChannel(
    channelUniqueName: string,
    message: string,
    showConfirmation?: boolean,
  ): Promise<void> {
    await this.chatEngineSendMessageService.sendTextMessage(channelUniqueName, message);
    if (showConfirmation && this.sessionService.isSessionActive()) {
      this.chatDialogsService.showSentSuccessfulMessage();
    }
  }
}
